// Lossless handling of large integers across the API boundary.
//
// `telemetryId` and the `before` cursor are `bigint` on the server, and a
// reading's value may be a large integer. `JSON.parse` converts every number to
// a double, so a value above 2^53 would be silently rounded *before* it could be
// validated. We therefore parse the response ourselves: a JSON integer outside
// the safe range is returned as an exact `bigint` wrapped in a tagged object,
// while everything else keeps the natural JSON shape. This mirrors the backend,
// which preserves integers exactly.

/** A JSON integer too large for a JavaScript double, carried exactly. */
export interface JsonBigInteger {
  readonly bigint: bigint;
}

/** Raised when a response cannot be parsed as JSON. */
export class JsonParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JsonParseError';
  }
}

/** Whether a value is an exact big-integer carrier. */
export function isJsonBigInteger(value: unknown): value is JsonBigInteger {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { bigint?: unknown }).bigint === 'bigint'
  );
}

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE = BigInt(Number.MIN_SAFE_INTEGER);

/**
 * Parse JSON text preserving integer precision.
 *
 * Representable data is returned as plain objects, arrays, strings, booleans,
 * `null`, and numbers. Only integers outside the safe range become
 * `JsonBigInteger` wrappers, which callers detect with `isJsonBigInteger`.
 */
export function parseJson(text: string): unknown {
  const parser = new Parser(text);
  const value = parser.parseValue();
  parser.skipWhitespace();
  if (!parser.atEnd()) {
    throw new JsonParseError(`unexpected trailing content at offset ${parser.offset}`);
  }
  return value;
}

class Parser {
  private index = 0;
  private readonly text: string;

  constructor(text: string) {
    this.text = text;
  }

  get offset(): number {
    return this.index;
  }

  atEnd(): boolean {
    return this.index >= this.text.length;
  }

  skipWhitespace(): void {
    while (this.index < this.text.length && isWhitespace(this.text[this.index])) {
      this.index += 1;
    }
  }

  parseValue(): unknown {
    this.skipWhitespace();
    const char = this.text[this.index];
    switch (char) {
      case '{':
        return this.parseObject();
      case '[':
        return this.parseArray();
      case '"':
        return this.parseString();
      case 't':
        return this.parseLiteral('true', true);
      case 'f':
        return this.parseLiteral('false', false);
      case 'n':
        return this.parseLiteral('null', null);
      default:
        return this.parseNumber();
    }
  }

  private parseObject(): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    this.expect('{');
    this.skipWhitespace();
    if (this.text[this.index] === '}') {
      this.index += 1;
      return result;
    }
    for (;;) {
      this.skipWhitespace();
      const key = this.parseString();
      this.skipWhitespace();
      this.expect(':');
      result[key] = this.parseValue();
      this.skipWhitespace();
      const char = this.text[this.index];
      if (char === ',') {
        this.index += 1;
        continue;
      }
      this.expect('}');
      return result;
    }
  }

  private parseArray(): unknown[] {
    const result: unknown[] = [];
    this.expect('[');
    this.skipWhitespace();
    if (this.text[this.index] === ']') {
      this.index += 1;
      return result;
    }
    for (;;) {
      result.push(this.parseValue());
      this.skipWhitespace();
      const char = this.text[this.index];
      if (char === ',') {
        this.index += 1;
        continue;
      }
      this.expect(']');
      return result;
    }
  }

  private parseString(): string {
    const start = this.index;
    this.expect('"');
    while (this.index < this.text.length) {
      const char = this.text[this.index];
      if (char === '\\') {
        this.index += 2;
        continue;
      }
      if (char === '"') {
        this.index += 1;
        // The slice is a valid JSON string literal; reuse JSON.parse for
        // escape handling rather than reimplementing it.
        return JSON.parse(this.text.slice(start, this.index)) as string;
      }
      this.index += 1;
    }
    throw new JsonParseError('unterminated string');
  }

  private parseLiteral<T>(literal: string, value: T): T {
    if (this.text.startsWith(literal, this.index)) {
      this.index += literal.length;
      return value;
    }
    throw new JsonParseError(`invalid literal at offset ${this.index}`);
  }

  private parseNumber(): number | JsonBigInteger {
    const start = this.index;
    if (this.text[this.index] === '-') this.index += 1;
    while (isDigit(this.text[this.index])) this.index += 1;

    let isInteger = true;
    if (this.text[this.index] === '.') {
      isInteger = false;
      this.index += 1;
      while (isDigit(this.text[this.index])) this.index += 1;
    }
    if (this.text[this.index] === 'e' || this.text[this.index] === 'E') {
      isInteger = false;
      this.index += 1;
      if (this.text[this.index] === '+' || this.text[this.index] === '-') this.index += 1;
      while (isDigit(this.text[this.index])) this.index += 1;
    }

    const literal = this.text.slice(start, this.index);
    if (literal === '' || literal === '-') {
      throw new JsonParseError(`invalid number at offset ${start}`);
    }

    if (isInteger) {
      const exact = BigInt(literal);
      if (exact > MAX_SAFE || exact < MIN_SAFE) {
        return { bigint: exact };
      }
    }
    const numeric = Number(literal);
    if (!Number.isFinite(numeric)) {
      throw new JsonParseError(`non-finite number at offset ${start}`);
    }
    return numeric;
  }

  private expect(char: string): void {
    if (this.text[this.index] !== char) {
      throw new JsonParseError(`expected ${char} at offset ${this.index}`);
    }
    this.index += 1;
  }
}

function isWhitespace(char: string | undefined): boolean {
  return char === ' ' || char === '\t' || char === '\n' || char === '\r';
}

function isDigit(char: string | undefined): boolean {
  return char !== undefined && char >= '0' && char <= '9';
}

/** Render a big-integer carrier or plain number as a decimal string. */
export function integerToString(value: number | JsonBigInteger): string {
  return isJsonBigInteger(value) ? value.bigint.toString() : String(value);
}

/** Compare two integer values exactly, across number and bigint forms. */
export function compareIntegers(a: number | JsonBigInteger, b: number | JsonBigInteger): number {
  const left = isJsonBigInteger(a) ? a.bigint : BigInt(a);
  const right = isJsonBigInteger(b) ? b.bigint : BigInt(b);
  return left < right ? -1 : left > right ? 1 : 0;
}

/** Normalize a reading value that may legitimately be a big integer. */
export function readingValueToDisplay(value: unknown): string | number | boolean | null {
  if (isJsonBigInteger(value)) return value.bigint.toString();
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
    return value;
  }
  return null;
}
